import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  type PortfolioMatrixJob,
  portfolioMatrixQueryKey,
} from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { guacHttpClient } from "@/lib/http";
import {
  CompanyIntakeSalesResponse,
  HttpResponse,
  UpdateCompanyIntakeSalesRequest,
} from "@/types";

/**
 * Updates a company's intake sales (deal team, sales owner, etc.). Used by the
 * portfolio matrix Sales cell, which only renders the add button on rows with no
 * sales yet, so sending a single-element dealTeam is correct there.
 */
export function useUpdatePortfolioSales() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      request,
    }: {
      companyId: string;
      request: UpdateCompanyIntakeSalesRequest;
    }) => {
      const res = await guacHttpClient.patch<
        CompanyIntakeSalesResponse,
        HttpResponse<CompanyIntakeSalesResponse>,
        UpdateCompanyIntakeSalesRequest
      >(`companies/${companyId}/company-intake/sales`, request);
      return res.data;
    },
    onSuccess: (data, { companyId }) => {
      const { dealTeam } = data;
      if (!dealTeam) return;

      // Deal team is per-company → patch every matrix row for this company in
      // place rather than refetching the expensive matrix.
      queryClient.setQueryData<PortfolioMatrixJob[]>(
        portfolioMatrixQueryKey,
        (jobs) =>
          jobs?.map((job) =>
            job.companyId === companyId ? { ...job, sales: dealTeam } : job,
          ),
      );
    },
  });
}
